import React, { useState, useEffect } from 'react';

import Footer from '../../components/footer/index';
import { Shield, Lock, Globe, Eye, Server, Users, CheckCircle, Play, Zap, MapPin, Clock, ArrowRight, MessageCircle } from 'lucide-react';

const VPNPage = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);
    
    const handleChange = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const vpnFeatures = [
    {
      icon: <Lock className="w-8 h-8" />,
      title: "End-to-End Encryption",
      description: "Enkripsi AES-256 military grade untuk melindungi semua data dan komunikasi Anda",
      color: "bg-blue-500"
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: "Zero Logs Policy",
      description: "Kami tidak menyimpan atau melacak aktivitas browsing dan data pribadi Anda",
      color: "bg-purple-500"
    },
    {
      icon: <Server className="w-8 h-8" />,
      title: "Global Server Network",
      description: "Akses ke 50+ server di berbagai negara untuk koneksi optimal dan bypassing geo-restrictions",
      color: "bg-green-500"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "High-Speed Connection",
      description: "Bandwidth unlimited dengan kecepatan tinggi tanpa throttling atau pembatasan",
      color: "bg-yellow-500"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Multi-Protocol Support",
      description: "Dukungan OpenVPN, WireGuard, IKEv2 untuk fleksibilitas dan keamanan maksimal",
      color: "bg-red-500"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Multi-Device Support",
      description: "Gunakan pada semua device Anda - Windows, Mac, iOS, Android, Linux",
      color: "bg-indigo-500"
    }
  ];

  const useCases = {
    personal: [
      {
        title: "Privacy Protection",
        description: "Lindungi identitas dan aktivitas online dari tracking dan surveillance",
        image: "/image/privacy_protection.png",
        benefits: ["Anonymous browsing", "IP masking", "ISP tracking protection"]
      },
      {
        title: "Public WiFi Security",
        description: "Aman browsing di WiFi publik dengan enkripsi end-to-end",
        image: "/image/publicwifi.png",
        benefits: ["Secure connections", "Data protection", "Man-in-middle attack prevention"]
      }
    ],
    business: [
      {
        title: "Remote Work Access",
        description: "Akses aman ke server perusahaan dan resource internal dari mana saja",
        image: "/image/remote.png",
        benefits: ["Secure remote access", "Internal network connection", "Data compliance"]
      },
      {
        title: "Team Collaboration",
        description: "Kolaborasi tim yang aman dengan akses terpusat ke aplikasi bisnis",
        image: "/image/team_colleraction.png",
        benefits: ["Centralized access", "Team management", "Activity monitoring"]
      }
    ],
    enterprise: [
      {
        title: "Corporate Security",
        description: "Solusi VPN enterprise dengan kontrol akses dan monitoring penuh",
        image: "/image/security1.png",
        benefits: ["Advanced security policies", "User management", "Compliance reporting"]
      },
      {
        title: "Site-to-Site Connection",
        description: "Koneksi aman antar kantor cabang dengan performa tinggi",
        image: "/image/site-to-site.png",
        benefits: ["Multi-site connectivity", "Dedicated bandwidth", "Redundancy support"]
      }
    ]
  };

  const vpnPackages = [
    {
      id: 1,
      name: "Personal VPN",
      popular: false,
      color: "from-blue-500 to-blue-700",
      devices: "5 Devices",
      servers: "25+ Servers",
      bandwidth: "Unlimited",
      features: [
        "AES-256 Encryption",
        "Zero Logs Policy",
        "Kill Switch Protection",
        "DNS Leak Protection",
        "24/7 Customer Support",
        "30-Day Money Back Guarantee"
      ]
    },
    {
      id: 2,
      name: "Business VPN",
      popular: true,
      color: "from-green-500 to-green-700",
      devices: "25 Devices",
      servers: "50+ Servers",
      bandwidth: "Unlimited",
      features: [
        "All Personal Features",
        "Dedicated IP Address",
        "Advanced Security Protocols",
        "Team Management Dashboard",
        "Priority Support",
        "Custom Configuration",
        "Multi-location Access"
      ]
    },
    {
      id: 3,
      name: "Enterprise VPN",
      popular: false,
      color: "from-purple-500 to-purple-700",
      devices: "Unlimited",
      servers: "Global Network",
      bandwidth: "Dedicated",
      features: [
        "All Business Features",
        "Site-to-Site VPN",
        "Dedicated Server Cluster",
        "Advanced Analytics",
        "SLA Guarantee 99.9%",
        "White-label Solution",
        "24/7 Technical Support",
        "Custom Integration"
      ]
    }
  ];

  const serverLocations = [
    { country: "Indonesia", city: "Jakarta", ping: "< 5ms", flag: "🇮🇩" },
    { country: "Singapore", city: "Singapore", ping: "< 15ms", flag: "🇸🇬" },
    { country: "United States", city: "New York", ping: "< 180ms", flag: "🇺🇸" },
    { country: "United Kingdom", city: "London", ping: "< 200ms", flag: "🇬🇧" },
    { country: "Japan", city: "Tokyo", ping: "< 100ms", flag: "🇯🇵" },
    { country: "Australia", city: "Sydney", ping: "< 120ms", flag: "🇦🇺" }
  ];

  const testimonials = [
    {
      name: "Budi Santoso",
      role: "IT Manager",
      company: "Tech Startup",
      rating: 5,
      comment: "VPN nya sangat stabil dan cepat. Tim remote kami bisa akses server perusahaan dengan aman tanpa masalah.",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face"
    },
    {
      name: "Sarah Wijaya",
      role: "Digital Nomad",
      company: "Freelancer",
      rating: 5,
      comment: "Sebagai freelancer yang sering pindah tempat, VPN ini membantu saya tetap aman saat menggunakan WiFi publik.",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=60&h=60&fit=crop&crop=face"
    },
    {
      name: "Ahmad Rizki",
      role: "CTO",
      company: "Manufacturing",
      rating: 5,
      comment: "Solusi enterprise VPN yang sangat reliable. Kantor pusat dan cabang tersambung dengan aman dan cepat.",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face"
    }
  ];

  const toggleDarkMode = () => setIsDarkMode(!isDarkMode);
  
  // Fungsi untuk mengarahkan ke WhatsApp dengan pesan otomatis
  const contactViaWhatsApp = (packageName = "VPN Service") => {
    const phoneNumber = "6287778882022";
    const message = `Halo, saya tertarik dengan layanan ${packageName}. Bisakah saya mendapatkan informasi lebih lanjut mengenai harga dan fiturnya?`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'
    }`}>
      
      
      
      {/* Hero Section */}
      <section
        className={`py-20 px-4 relative overflow-hidden ${
          isDarkMode ? 'bg-gray-900' : ''
        }`}
        style={{
          backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
          backgroundImage: isDarkMode
            ? "linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('/image/vpn/vpn-hero-dark.jpg')"
            : "linear-gradient(rgba(255,255,255,0.8), rgba(255,255,255,0.8)), url('/image/vpn/vpn-hero.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container mx-auto relative z-10 text-center">
          <div className={`inline-flex items-center gap-2 ${
            isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-[#1E9B4C]'
          } px-4 py-2 rounded-full text-sm font-medium mb-6`}>
            <Shield className="w-4 h-4" />
            VPN Security Solutions
          </div>

          <h1 className={`text-4xl md:text-6xl font-bold mb-6 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Browse Safely &
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#1E9B4C] to-green-700">
              Privately
            </span>
          </h1>

          <p className={`text-xl max-w-3xl mx-auto mb-8 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            VPN (Virtual Private Network) kami memastikan keamanan dan privasi dalam penggunaan internet, 
            memungkinkan akses yang aman ke jaringan pribadi dari jarak jauh serta mengenkripsi data 
            untuk melindungi informasi sensitif
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => contactViaWhatsApp("VPN Trial")}
              className="bg-[#1E9B4C] hover:bg-green-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Coba Gratis 7 Hari
            </button>
          </div>
        </div>
      </section>

      <div className="absolute inset-0">
        <img
          src="https://telnet.net.id/assets/bg.png"
          alt="Background pattern"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-transparent dark:via-black/40"></div>
      </div>

      {/* Features Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Mengapa Memilih VPN Kami?
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Keamanan tingkat enterprise dengan kemudahan penggunaan untuk semua
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {vpnFeatures.map((feature, index) => (
              <div
                key={index}
                className={`group p-6 rounded-xl shadow-lg overflow-hidden transform hover:scale-105 hover:shadow-2xl transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 hover:border-[#1E9B4C] shadow-gray-900/50' 
                    : 'bg-white border border-gray-100 hover:border-[#1E9B4C] shadow-gray-300/20'
                }`}
              >
                <div className={`w-16 h-16 rounded-lg flex items-center justify-center text-white mb-4 ${
                  feature.color || 'bg-[#1E9B4C]'
                }`}>
                  {feature.icon}
                </div>

                <h3 className={`text-xl font-bold mb-3 transition-colors ${
                  isDarkMode
                    ? 'text-white group-hover:text-[#1E9B4C]'
                    : 'text-gray-900 group-hover:text-[#1E9B4C]'
                }`}>
                  {feature.title}
                </h3>

                <p className={`transition-colors ${
                  isDarkMode 
                    ? 'text-gray-300 group-hover:text-gray-200' 
                    : 'text-gray-600 group-hover:text-gray-700'
                }`}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Solusi untuk Berbagai Kebutuhan
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Temukan solusi VPN yang tepat untuk kebutuhan spesifik Anda
            </p>
          </div>

          <div className="flex justify-center mb-8">
            <div className={`inline-flex rounded-lg p-1 shadow-lg ${
              isDarkMode 
                ? 'bg-gray-800 border border-gray-700' 
                : 'bg-white border border-gray-200'
            }`}>
              {Object.keys(useCases).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    activeTab === tab
                      ? 'bg-[#1E9B4C] text-white shadow-lg'
                      : isDarkMode
                      ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {tab === 'personal'
                    ? 'Personal'
                    : tab === 'business'
                    ? 'Business'
                    : 'Enterprise'}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases[activeTab].map((useCase, index) => (
              <div
                key={index}
                className={`rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 shadow-gray-900/50' 
                    : 'bg-white border border-gray-100 shadow-gray-300/20'
                }`}
              >
                <div className="relative">
                  <img
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className={`absolute inset-0 ${
                    isDarkMode 
                      ? 'bg-gradient-to-t from-gray-800/20 to-transparent' 
                      : 'bg-gradient-to-t from-white/20 to-transparent'
                  }`}></div>
                </div>
                <div className="p-6">
                  <h3 className={`text-xl font-bold mb-3 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {useCase.title}
                  </h3>
                  <p className={`mb-4 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {useCase.description}
                  </p>
                  <div className="space-y-2">
                    {useCase.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#1E9B4C] flex-shrink-0" />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {benefit}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Server Locations */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Server Locations
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Akses ke server berkualitas tinggi di berbagai belahan dunia
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serverLocations.map((location, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl border-2 transition-all duration-300 hover:border-[#1E9B4C] hover:shadow-lg transform hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 shadow-gray-900/50 hover:shadow-xl hover:bg-gray-750' 
                    : 'bg-white border-gray-200 shadow-gray-300/20 hover:shadow-xl hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{location.flag}</span>
                    <div>
                      <h3 className={`font-bold transition-colors ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {location.country}
                      </h3>
                      <p className={`text-sm transition-colors ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {location.city}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4 text-[#1E9B4C]" />
                    <span className="text-sm font-medium text-[#1E9B4C]">{location.ping}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-sm transition-colors ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Status
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-green-500">Online</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Pricing Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Pilih Paket VPN Anda
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Paket yang fleksibel sesuai kebutuhan personal hingga enterprise
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {vpnPackages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative rounded-2xl p-8 shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                  pkg.popular
                    ? `bg-gradient-to-br ${pkg.color} text-white shadow-2xl transform scale-105`
                    : isDarkMode
                    ? 'bg-gray-800 text-white border border-gray-700 shadow-gray-900/50'
                    : 'bg-white text-gray-900 border border-gray-200 shadow-gray-300/20'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                  <div className="mb-4">
                    <span className="text-lg font-medium">Harga khusus untuk Anda</span>
                  </div>
                </div>

                <div className="mb-8">
                  <div className={`flex justify-between mb-2 pb-2 border-b ${
                    pkg.popular 
                      ? 'border-white/20' 
                      : isDarkMode 
                      ? 'border-gray-700' 
                      : 'border-gray-200'
                  }`}>
                    <span>Devices</span>
                    <span className="font-bold">{pkg.devices}</span>
                  </div>
                  <div className={`flex justify-between mb-2 pb-2 border-b ${
                    pkg.popular 
                      ? 'border-white/20' 
                      : isDarkMode 
                      ? 'border-gray-700' 
                      : 'border-gray-200'
                  }`}>
                    <span>Servers</span>
                    <span className="font-bold">{pkg.servers}</span>
                  </div>
                  <div className={`flex justify-between mb-4 pb-2 border-b ${
                    pkg.popular 
                      ? 'border-white/20' 
                      : isDarkMode 
                      ? 'border-gray-700' 
                      : 'border-gray-200'
                  }`}>
                    <span>Bandwidth</span>
                    <span className="font-bold">{pkg.bandwidth}</span>
                  </div>

                  <ul className="space-y-3">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          pkg.popular ? 'text-white' : 'text-[#1E9B4C]'
                        }`} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => contactViaWhatsApp(pkg.name)}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 transform hover:scale-105 ${
                    pkg.popular 
                      ? 'bg-white text-green-700 hover:bg-gray-100 shadow-lg' 
                      : 'bg-[#1E9B4C] text-white hover:bg-green-700 shadow-lg hover:shadow-xl'
                  }`}
                >
                  <MessageCircle className="w-5 h-5" />
                  Hubungi Kami
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-[#1E9B4C]'
      }`}>
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Mulai Browsing dengan Aman Hari Ini
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Dapatkan perlindungan VPN terbaik dengan trial gratis 7 hari. Tidak ada komitmen, batalkan kapan saja.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => contactViaWhatsApp("VPN Trial")}
              className="bg-white text-[#1E9B4C] hover:bg-gray-100 font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <MessageCircle className="w-5 h-5" />
              Coba Gratis 7 Hari
            </button>
            <button 
              onClick={() => contactViaWhatsApp("VPN Enterprise")}
              className="border-2 border-white text-white hover:bg-white hover:text-[#1E9B4C] font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <ArrowRight className="w-5 h-5" />
              Hubungi Sales
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VPNPage;